import { dashboardApi } from '@/api';
import { PeriodOption } from '@/config/model';
import { FeatureFlagWithStatus } from '@/features/Casl';
import { queryOptions } from '@tanstack/react-query';

interface ProjectInfoQueryArgs {
    projectId: string;
    dashboardId: string | undefined;
}

interface ProjectUser {
    displayName: string;
    email: string;
}

interface Config {
    id: string;
    key: string;
    type: string;
    value: string;
}

interface Rule {
    id: string;
    channel: string;
    type: string;
    subtype: string;
    value: string;
    target: string;
}

interface MediaPrice {
    channel: string;
    prices: number[];
}

interface DashboardConfigDto {
    id: string;

    name: string;

    modelStartDate: string | null;

    modelEndDate: string | null;

    dashboardVariables: ProjectConfigDashboardVariableDto[];

    dataPeriodMaxDate: string;

    dataPeriodMinDate: string;

    yearlyBudgetOptimisationPeriod: string;

    competitorVariableName: string;

    campaignDefaultStartDate: Date;

    campaignDefaultEndDate: Date;

    campaignMinStartDate: Date;

    campaignMaxEndDate: Date;

    campaignDefaultBudget: number;

    modeledVariablesOnlyInTotalMediaRoi: boolean;

    featureFlags: FeatureFlagWithStatus[];

    configs: Config[];

    rules: Rule[];

    mediaPrices?: MediaPrice[];
}

export enum MegastarFileType {
    input = 'input',
    output = 'output',
}

interface ProjectConfigDashboardVariableDto {
    variableName: string;
    displayLabel: string | null;
    competitor: boolean;
    isTotal: boolean;
    fileType: MegastarFileType;
}

export type RoiType =
    | 'RETURN_RATIO'
    | 'TURNOVER_RATIO'
    | 'REVENUE_RATIO'
    | 'UNITS_SOLD'
    | 'UNITS_SOLD/1000'
    | 'UNITS_SOLD/100'
    | 'COST_PER_UNIT';

export type ContributionFormatting = 'none' | 'eur' | 'usd' | 'pcs';

export interface ProjectDetailsDto {
    id: string;
    name: string;
    startDate: string;
    endDate: string;
    lastUpdatedAt: string;
    kpiDefinition: string;
    projectInfo: string;
    interval: PeriodOption;
    roiType: RoiType;
    frequency: string;
    status: string;
    dataScientist: ProjectUser;
    projectLead: ProjectUser;
    contributionFormatting: ContributionFormatting;
}

export type ProjectInfoResponse = {
    project: ProjectDetailsDto;
    dashboard: DashboardConfigDto;
};

const getProjectInfo = async ({
    projectId,
    dashboardId,
}: ProjectInfoQueryArgs) => {
    return dashboardApi<ProjectInfoResponse>({
        method: 'GET',
        url: `/front-office/project/${projectId}`,
        params: {
            dashboardId,
        },
    });
};

export const getProjectInfoQueryOptions = (args: ProjectInfoQueryArgs) =>
    queryOptions({
        queryKey: ['project-info', args],
        queryFn: () => getProjectInfo(args),
        staleTime: Infinity,
    });
